import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { routes } from 'constants/config';
import useFetch from 'components/shared/hooks/useFetch';
import { Skeleton } from 'ui';
import Chat from './Answers/Chat';
import Recording from './Answers/Recording';
import Written from './Answers/Written';
import TicketHandling from './Answers/TicketHandling';
import EmailComposition from './Answers/EmailComposition';
import EmailReply from './Answers/EmailReply';

const AnswerFeedback = () => {
  const { id } = useParams();
  const {
    data = {},
    loading,
    error,
  } = useFetch(routes.ANSWER_FROM_LEARNER_SEEKING_FEEDBACK(id));

  useEffect(() => {
    if (error) {
      throw new Error(error);
    }
  }, [error]);

  const {
    userName = '',
    answer = {},
    question = {},
    companyLogo = '',
    recording = {},
    ziggeoApiKey,
    hasZiggeoForAllMedia,
  } = data;
  const { subject: questionSubject } = question;
  const questionData = question.raw_choices
    ? JSON.parse(question.raw_choices)
    : {};
  questionData.companyLogo = companyLogo;
  const { question_type: questionType, given_answer: givenAnswer } = answer;

  const adjustedGivenAnswer = () => {
    if (!givenAnswer) return '';
    switch (questionType) {
      case 'ticket_handling':
        return givenAnswer.message;
      case 'email_composition':
        return givenAnswer.subject;
      default:
        return givenAnswer;
    }
  };

  const answerText = adjustedGivenAnswer();
  const body = questionType === 'email_composition' ? givenAnswer?.body : '';

  const renderBody = () => {
    switch (questionType) {
      case 'webcam_recording':
      case 'screen_recording':
      case 'audio_recording':
        return (
          <Recording
            ziggeoApiKey={ziggeoApiKey}
            recording={recording}
            hasZiggeoForAllMedia={hasZiggeoForAllMedia}
          />
        );
      case 'written_response':
        return <Written answerText={answerText} />;
      case 'email_composition':
        return <EmailComposition answerText={answerText} body={body} />;
      case 'ticket_handling':
        return (
          <TicketHandling
            questionData={questionData}
            answer={answer}
            answerText={answerText}
          />
        );
      case 'chat':
        return <Chat answerText={answerText} questionData={questionData} />;
      case 'email_reply':
        return (
          <EmailReply answerText={answerText} questionData={questionData} />
        );
      default:
        return (
          <div className="Card p-32 mt-24 mb-24">
            {answerText}
            {body}
          </div>
        );
    }
  };

  return (
    <div className="ff-ss mw-700 mx-a">
      <div className="m-p-24 mt-32">
        <span className="fz-24">{t('practice.answers.title')}</span>
        <hr className="mt-8 mb-12 border-gray-light" />
        <span className="fz-24 c-brand-night fw-b">{questionSubject}</span>
        <p className="fz-18">{userName}</p>
        {loading ? (
          <>
            <Skeleton className="mb-4 pt-32" height={40} width="90%" />
            <Skeleton className="mb-32" width="30%" />
            <Skeleton className="mb-4" height={100} />
          </>
        ) : (
          renderBody()
        )}
      </div>
    </div>
  );
};

AnswerFeedback.propTypes = {
  data: PropTypes.shape({
    userName: PropTypes.string,
    answer: PropTypes.shape({
      question_type: PropTypes.string,
      given_answer: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          message: PropTypes.string,
          subject: PropTypes.string,
          body: PropTypes.string,
        }),
      ]),
    }),
    embedUrl: PropTypes.string,
    question: PropTypes.shape({
      subject: PropTypes.string,
      id: PropTypes.number,
      raw_choices: PropTypes.string,
    }),
  }),
};

export default AnswerFeedback;
